// BoardModel.ts
export type Rect = { x:number; y:number; w:number; h:number };
export type HoleAbs = { cx:number; cy:number; r:number; pts:1|3|5 };

export const baseWidth = 220;

export class BoardModel {
  // ---- Geometry knobs (moved from index.tsx) ----
  boardW = 260;
  boardH = 900;
  get aspect() { return this.boardH / this.boardW; }

  framePad = 10;
  moatPad  = 6;
  playPad  = 8;
  slabHeightRatio = 0.55;

  // Ball size as a fraction of play width (used by Skeech.resize)
  ballRadiusFrac = 0.04;

  // Chevrons + holes live here
  chevrons = {
    five:  { leftEdge: 0.21, rightEdge: 0.79, apexDepth: 0.09, gap: -0.01 },
    three: { leftEdge: 0.10, rightEdge: 0.90, apexDepth: 0.11, gap: 0.02 },
    one:   { leftEdge: 0.01, rightEdge: 0.99, apexDepth: 0.13, gap: 0.06 },
  } as const;

  holeSpec = [
    { x: 0.10, y: 0.30, rFrac: 0.09, pts: 1 as const },
    { x: 0.90, y: 0.30, rFrac: 0.09, pts: 1 as const },
    { x: 0.30, y: 0.19, rFrac: 0.06, pts: 3 as const },
    { x: 0.70, y: 0.19, rFrac: 0.06, pts: 3 as const },
    { x: 0.50, y: 0.08, rFrac: 0.04, pts: 5 as const },
  ];

  // ---- Helpers ----
  rects(canvas: HTMLCanvasElement, dpr: number) {
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const frame: Rect = { x: 0, y: 0, w: W, h: H };
    const moat:  Rect = { x: this.framePad, y: this.framePad, w: W - 2 * this.framePad, h: H - 2 * this.framePad };
    const play:  Rect = { x: moat.x + this.moatPad, y: moat.y + this.moatPad, w: moat.w - 2 * this.moatPad, h: moat.h - 2 * this.moatPad };
    const slab:  Rect = { x: play.x + this.playPad, y: play.y + this.playPad, w: play.w - 2 * this.playPad, h: Math.round(play.h * this.slabHeightRatio) };
    return { frame, moat, play, slab };
  }

  holesAbs(slab: Rect): HoleAbs[] {
    return this.holeSpec.map(h => ({
      cx: slab.x + h.x * slab.w,
      cy: slab.y + h.y * slab.h,
      r:  h.rFrac * slab.w,
      pts: h.pts,
    }));
  }

  holeGroups() {
    const ones   = this.holeSpec.filter(h => h.pts === 1);
    const threes = this.holeSpec.filter(h => h.pts === 3);
    const fives  = this.holeSpec.filter(h => h.pts === 5);
    const avg = (arr:number[]) => arr.reduce((a,b)=>a+b,0) / Math.max(arr.length,1);
    return { y1: avg(ones.map(h => h.y)), y3: avg(threes.map(h => h.y)), y5: avg(fives.map(h => h.y)) };
  }

  resize(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, dpr: number) {
    const aspect = this.aspect;

    // Let the parent decide available space; we’ll fill it
    const container = canvas.parentElement as HTMLElement;
    const availW = Math.max(260, container?.clientWidth  || window.innerWidth  - 24);
    const availH = Math.max(320, container?.clientHeight || window.innerHeight - 24);

    // Height-first: make it as tall as we can, then compute W by aspect
    let H = Math.floor(availH);
    let W = Math.floor(H / aspect);

    // If width would overflow, clamp to width and recompute height
    if (W > availW) { W = Math.floor(availW); H = Math.floor(W * aspect); }

    canvas.width  = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width  = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

}
